import { Injectable, type Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, type Observable } from 'rxjs';
import { StorageService } from '@comparit/infrastructure';

const TASK_CONTEXT_KEY = 'task-context';

interface TaskContextStorage {
  taskId: string | null;
  vorgangId: string | null;
}

/**
 * Working handles of the task identity model: the `token` in a save/restore
 * link is a one-time credential; `task/restore` redeems it and everything
 * afterwards (task updates, antragsfragen prefill, vorgang resume) addresses
 * the task via `taskId`. `vorgangId` marks a possibly resumable Antrag.
 *
 * Neither value may ever appear in a URL. Persisted through StorageService,
 * so the context survives reloads but is wiped on fresh visits — a new
 * session must not keep updating an old task.
 *
 * Subjects are the source of truth: they emit synchronously, independent of
 * change detection, which the background sync pipeline relies on. The
 * signals are derived views for templates and computed state.
 */
@Injectable({ providedIn: 'root' })
export class TaskContextService {
  private readonly storage = inject(StorageService);

  private readonly _taskId$ = new BehaviorSubject<string | null>(
    this.storage.getData<TaskContextStorage>(TASK_CONTEXT_KEY)?.taskId ?? null
  );
  private readonly _vorgangId$ = new BehaviorSubject<string | null>(
    this.storage.getData<TaskContextStorage>(TASK_CONTEXT_KEY)?.vorgangId ?? null
  );

  readonly taskId$: Observable<string | null> = this._taskId$.asObservable();

  readonly taskId: Signal<string | null> = toSignal(this._taskId$, { requireSync: true });
  readonly vorgangId: Signal<string | null> = toSignal(this._vorgangId$, { requireSync: true });
  readonly hasTask = computed(() => this.taskId() !== null);

  initFromRestore(taskId: string, vorgangId: string | null): void {
    this._taskId$.next(taskId);
    this._vorgangId$.next(vorgangId);
    this.persist();
  }

  setTaskId(taskId: string): void {
    this._taskId$.next(taskId);
    this.persist();
  }

  clearVorgangId(): void {
    this._vorgangId$.next(null);
    this.persist();
  }

  clear(): void {
    this._taskId$.next(null);
    this._vorgangId$.next(null);
    this.persist();
  }

  private persist(): void {
    this.storage.save<TaskContextStorage>(TASK_CONTEXT_KEY, {
      taskId: this._taskId$.getValue(),
      vorgangId: this._vorgangId$.getValue(),
    });
  }
}
